import { Typography } from '@material-ui/core';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { Network } from '@mogul/api-client';
import { BigNumber } from 'ethers';
import { formatEther } from 'ethers/lib/utils';
import type { FC } from 'react';
import { useMemo } from 'react';

import { ReactComponent as BidIcon } from '../../../common/svg/general/icon-auction.svg';
import { ReactComponent as CanceledByIcon } from '../../../common/svg/general/icon-cancelled.svg';
import { ReactComponent as CrownIcon } from '../../../common/svg/general/icon-crown.svg';
import { ReactComponent as ExternalLink } from '../../../common/svg/general/icon-external-link.svg';
import { ReactComponent as MintedIcon } from '../../../common/svg/general/icon-minted.svg';
import { ReactComponent as ListedByIcon } from '../../../common/svg/general/listed-by-icon.svg';
import { useWeb3State } from '../../../hooks';
import {
  getIsEthListing,
  getIsStarsListing,
  getListingEthPrice,
  getListingStarsPrice,
} from '../../../lib/graph/graph-utils';
import type { AssetHistory } from '../../../lib/graph/MogulMarketplace';
import {
  AssetHistoryType,
  ListingStatus,
} from '../../../lib/graph/MogulMarketplace';
import {
  getNetworkCurrencySymbol,
  getStarsAddress,
} from '../../../lib/smart-contract-utils';
import styles from './NftHistoryRow.module.css';

// temporary fix of the subgraph issue
// (sale.amount is always 0, check mogul-subgraphs-v2/src/mappings/marketplaceCommon/createSaleMade soldPrice)
const getSalePrice = (sale: AssetHistory['sale']) => {
  const listingPrices = sale?.listing?.listingPrices;
  let price = '0';

  if (sale && listingPrices) {
    const foundPrice = listingPrices.find(
      listingPrice => listingPrice.listingToken === sale.auctionToken,
    )?.price;

    if (foundPrice) {
      price = foundPrice;
    }
  }

  return formatEther(BigNumber.from(price));
};

export const NftHistoryRowComponent: FC<AssetHistory> = ({
  type,
  timestamp,
  hash,
  bid,
  listing,
  sale,
  transfer,
}) => {
  const { walletDetails } = useWeb3State();
  const formattedDate = new Date(timestamp * 1000).toLocaleDateString();
  const coinSymbol = getNetworkCurrencySymbol();

  const explorerLink = `${walletDetails.chain.explorerUrl}/tx/${hash}`;

  let icon;
  let header;
  let walletAddress;
  let isHighestBid;
  let priceString = 'View details';

  const [isEthListing, isStarsListing, listingEthPrice, listingStarsPrice] =
    useMemo(
      () =>
        listing
          ? [
              getIsEthListing(listing),
              getIsStarsListing(listing),
              getListingEthPrice(listing),
              getListingStarsPrice(listing),
            ]
          : [false, false, listing, listing],
      [listing],
    );

  switch (type) {
    case AssetHistoryType.AuctionStart:
      icon = <ListedByIcon />;
      header = 'Auctioned by';
      walletAddress = listing?.seller.address as string;
      priceString =
        formatEther(
          BigNumber.from(isStarsListing ? listingStarsPrice : listingEthPrice),
        ) +
        ' ' +
        (isStarsListing ? 'MOGUL' : coinSymbol);
      break;
    case AssetHistoryType.AuctionBid:
      icon = <BidIcon />;
      header = 'Bid by';
      walletAddress = bid?.bidder.address as string;
      isHighestBid = bid?.amount === bid?.listing.highestBidAmount && (
        <>
          <Typography
            variant="body2"
            color="primary"
            style={{ fontSize: 16, opacity: 1, marginRight: '0.2em' }}
          >
            Highest Bid
          </Typography>
          <Typography
            variant="body2"
            style={{ fontSize: 16, marginRight: '0.2em' }}
          >
            &bull;
          </Typography>
        </>
      );
      priceString =
        formatEther(BigNumber.from(bid?.amount)) +
        ' ' +
        (bid && getIsStarsListing(bid.listing) ? 'MOGUL' : coinSymbol);
      break;
    case AssetHistoryType.AuctionCancel:
    case AssetHistoryType.AuctionEnd:
      icon =
        listing?.highestBidder?.address === listing?.seller.address ||
        listing?.status === ListingStatus.Cancelled ? (
          <CanceledByIcon />
        ) : (
          <CrownIcon />
        );
      header =
        listing?.highestBidder?.address === listing?.seller.address ||
        listing?.status === ListingStatus.Cancelled
          ? 'Auction cancelled by'
          : 'Sold to';
      walletAddress = listing?.highestBidder?.address as string;
      priceString =
        formatEther(BigNumber.from(listing?.highestBidAmount)) +
        ' ' +
        (isStarsListing ? 'MOGUL' : coinSymbol);
      break;
    case AssetHistoryType.ListingStart:
      icon = <ListedByIcon />;
      header = 'Listed by';
      walletAddress = listing?.seller.address as string;
      priceString =
        formatEther(
          BigNumber.from(isEthListing ? listingEthPrice : listingStarsPrice),
        ) +
        ' ' +
        (isEthListing ? coinSymbol : 'MOGUL');
      break;
    case AssetHistoryType.ListingPriceChange:
      icon = <ListedByIcon />;
      header = 'Listing price changed by';
      walletAddress = listing?.seller.address as string;
      priceString =
        formatEther(
          BigNumber.from(isStarsListing ? listingStarsPrice : listingEthPrice),
        ) +
        ' ' +
        (isStarsListing ? 'MOGUL' : coinSymbol);
      break;
    case AssetHistoryType.Sale:
      icon = <CrownIcon />;
      header = 'Sold to';
      walletAddress = sale?.buyer.address as string;
      priceString =
        getSalePrice(sale) +
        ' ' +
        (sale?.auctionToken === getStarsAddress().toLowerCase()
          ? 'MOGUL'
          : coinSymbol);
      break;
    case AssetHistoryType.ListingCancel:
      icon = <CanceledByIcon />;
      header = 'Listing cancelled by';
      walletAddress = listing?.seller.address as string;
      priceString =
        formatEther(
          BigNumber.from(isStarsListing ? listingStarsPrice : listingEthPrice),
        ) +
        ' ' +
        (isStarsListing ? 'MOGUL' : coinSymbol);
      break;
    case AssetHistoryType.Transfer:
      icon = <SwapHorizIcon style={{ color: 'gold' }} />;
      header = 'Transferred to';
      walletAddress = transfer?.to.address as string;
      break;
    case AssetHistoryType.Create:
      icon = <MintedIcon />;
      header = 'Minted to';
      walletAddress = transfer?.to.address as string;
      break;
    default:
      break;
  }

  return (
    <div className={`${styles.nftHistoryRowContainer} md:flex`}>
      <div className="flex flex-col md:flex-row">
        <div className="flex items-center mr-3">
          <div
            className={`flex justify-center items-center h-6 w-6 ${styles.nftHistoryRowIcon}`}
          >
            {icon}
          </div>
        </div>
        <div>
          <div className="md:flex items-center">
            <Typography
              variant="body2"
              style={{
                fontSize: 16,
                marginRight: '0.5em',
              }}
            >
              {header}
            </Typography>
            <Typography
              style={{ fontSize: 16, opacity: 1 }}
              className="truncate"
            >
              {walletAddress}
            </Typography>
          </div>
          <div className="md:flex">
            {isHighestBid}
            <Typography variant="body2" style={{ fontSize: 16 }}>
              {formattedDate}
            </Typography>
          </div>
        </div>
      </div>
      <Typography style={{ color: '#FCD559' }} component={'span'}>
        <a
          href={explorerLink}
          target="_blank"
          rel="noreferrer"
          className="flex items-center hover:underline"
        >
          <div className="mr-2">{priceString}</div>
          <ExternalLink style={{ color: '#FCD559' }} />
        </a>
      </Typography>
    </div>
  );
};
